'use client';

import Link from 'next/link';
import { Hotel, Twitter, Linkedin, Instagram, ShieldCheck, Lock, Globe } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { LocaleLink } from '@/core/i18n';
import { usePageContent } from '@/core/i18n/content';
import { FOOTER_CONTENT, type FooterContent } from '@/core/i18n/content/footer.content';
import { useTranslation } from '@/core/i18n';

interface FooterProps {
  className?: string;
}

/* ── Link href definitions (labels come from content dictionary) ── */

const productHrefs = ['/features', '/pricing', '/partner-program', '/about', '/resilience'] as const;
const solutionHrefs = [
  '/hotel-management-software', '/hotel-pms-software', '/hotel-channel-manager',
  '/hotel-booking-engine', '/hotel-crm', '/hotel-revenue-management',
] as const;
const companyHrefs = ['/about', '/contact', '/blog', '/register-hotel'] as const;
const supportHrefs = ['/partner-program', 'https://bookmyroom.io'] as const;
const propertyTypeHrefs = [
  '/hotel-software-for-small-hotels', '/hotel-software-for-boutique-hotels',
  '/hotel-software-for-resorts', '/hotel-software-for-hotel-chains', '/hotel-software-for-hostels',
] as const;
const compareHrefs = [
  '/hotelstack-vs-cloudbeds', '/hotelstack-vs-hotelogix', '/hotelstack-vs-mews',
  '/hotelstack-vs-opera', '/hotelstack-vs-rms-cloud', '/hotelstack-vs-siteminder',
  '/hotelstack-vs-roomraccoon', '/hotelstack-vs-innroad', '/hotelstack-vs-webrezpro',
] as const;
const integrationHrefs = [
  '/integrations/stripe', '/integrations/booking-com', '/integrations/expedia',
  '/integrations/airbnb', '/integrations/agoda', '/integrations/amadeus-gds',
  '/integrations/sabre-gds', '/integrations/paypal', '/integrations/paymenthooks',
  '/integrations/eventhooks',
] as const;
const integrationLabels = [
  'Stripe', 'Booking.com', 'Expedia', 'Airbnb', 'Agoda',
  'Amadeus GDS', 'Sabre GDS', 'PayPal', 'PaymentHooks', 'EventHooks',
] as const;
const cityHrefs = [
  '/hotel-software-dubai', '/hotel-software-bangkok', '/hotel-software-london',
  '/hotel-software-new-york', '/hotel-software-singapore', '/hotel-software-paris',
  '/hotel-software-tokyo', '/hotel-software-istanbul', '/hotel-software-sydney',
  '/hotel-software-los-angeles',
] as const;
const complianceHrefs = [
  '/compliance/eu-uk', '/compliance/united-states', '/compliance/uae-middle-east',
  '/compliance/thailand-sea', '/compliance/australia', '/compliance/japan', '/compliance/india',
] as const;

/* Compare link labels — brand names stay, only "vs" needs no translation */
const compareLabels = [
  'HotelStack vs Cloudbeds', 'HotelStack vs Hotelogix', 'HotelStack vs Mews',
  'HotelStack vs OPERA', 'HotelStack vs RMS Cloud', 'HotelStack vs SiteMinder',
  'HotelStack vs RoomRaccoon', 'HotelStack vs innRoad', 'HotelStack vs WebRezPro',
] as const;

const socialLinks = [
  { href: 'https://twitter.com/hotelstack', label: 'Twitter', icon: Twitter },
  { href: 'https://linkedin.com/company/hotelstack', label: 'LinkedIn', icon: Linkedin },
  { href: 'https://instagram.com/hotelstack', label: 'Instagram', icon: Instagram },
] as const;

/* ── FooterColumn ── */

function FooterColumn({
  title,
  hrefs,
  labels,
  idPrefix,
}: {
  title: string;
  hrefs: readonly string[];
  labels: readonly string[];
  idPrefix?: string;
}) {
  return (
    <div>
      <h4 className="text-[12px] font-semibold text-hms-textSub uppercase tracking-widest mb-4">
        {title}
      </h4>
      <ul className="space-y-2.5">
        {hrefs.map((href, i) => (
          <li key={href}>
            {href.startsWith('http') ? (
              <Link
                id={idPrefix ? `${idPrefix}-${href.replace(/\//g, '-').replace(/^-/, '')}` : undefined}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13px] text-hms-textDim hover:text-hms-gold transition-colors duration-200"
              >
                {labels[i] ?? ''}
              </Link>
            ) : (
              <LocaleLink
                id={idPrefix ? `${idPrefix}-${href.replace(/\//g, '-').replace(/^-/, '')}` : undefined}
                href={href}
                className="text-[13px] text-hms-textDim hover:text-hms-gold transition-colors duration-200"
              >
                {labels[i] ?? ''}
              </LocaleLink>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Footer ── */

export function Footer({ className }: FooterProps) {
  const c = usePageContent(FOOTER_CONTENT);
  const { t } = useTranslation();
  return (
    <footer
      id="footer"
      className={cn(
        'border-t border-hms-border bg-hms-sidebar',
        className
      )}
    >
      {/* ── Main Grid ── */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <LocaleLink href="/" className="flex items-center gap-2.5 group mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-hms-gold to-hms-goldLt flex items-center justify-center shadow-md shadow-hms-gold/20 transition-shadow group-hover:shadow-hms-gold/40">
                <Hotel className="w-4 h-4 text-black" strokeWidth={2.5} />
              </div>
              <span className="font-semibold text-hms-text text-[15px] tracking-tight">
                {t('shared.footer.brandHotel')}<span className="text-hms-gold">{t('shared.footer.brandStack')}</span>
              </span>
            </LocaleLink>
            <p className="text-[13px] text-hms-textDim leading-relaxed mb-6 max-w-xs">
              {c.brand}
            </p>

            {/* Social Icons */}
            <div className="flex items-center gap-2">
              {socialLinks.map((social) => (
                <Link
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/[0.04] border border-hms-border hover:border-hms-gold/40 text-hms-textDim hover:text-hms-gold transition-all duration-200"
                >
                  <social.icon className="w-4 h-4" />
                </Link>
              ))}
            </div>
          </div>

          {/* Product Column */}
          <FooterColumn title={c.columns.platform} hrefs={productHrefs} labels={c.links.product} />

          {/* Solutions Column */}
          <FooterColumn title={c.columns.solutions} hrefs={solutionHrefs} labels={c.links.solutions} />

          {/* Company Column */}
          <FooterColumn title={c.columns.company} hrefs={companyHrefs} labels={c.links.company} />

          {/* Legal Column */}
          <FooterColumn title={c.columns.legalSupport} hrefs={supportHrefs} labels={c.links.support} />
        </div>

        {/* ── Secondary Links Row ── */}
        <div className="border-t border-hms-border/50 mt-10 pt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          <FooterColumn title={c.columns.byPropertyType} hrefs={propertyTypeHrefs} labels={c.links.propertyTypes} idPrefix="hs-footer" />
          <FooterColumn title={c.columns.compare} hrefs={compareHrefs} labels={compareLabels} idPrefix="hs-footer" />
          <FooterColumn title={c.columns.integrations} hrefs={integrationHrefs} labels={integrationLabels} idPrefix="hs-footer" />
          <FooterColumn title={c.columns.byCity} hrefs={cityHrefs} labels={c.links.cities} idPrefix="hs-footer" />
          <FooterColumn title={c.columns.compliance} hrefs={complianceHrefs} labels={c.links.compliance} idPrefix="hs-footer" />
        </div>
      </div>

      {/* ── Trust Badges ── */}
      <div className="border-t border-hms-border/50">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-6">
          <div id="footer-trust-badges" className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            <LocaleLink href="/security" className="flex items-center gap-2 group">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-hms-green/10 border border-hms-green/20 group-hover:border-hms-green/40 transition-colors">
                <ShieldCheck className="h-4 w-4 text-hms-green" />
              </div>
              <div>
                <p id="footer-badge-soc2" className="text-[11px] font-bold text-hms-text leading-none">SOC 2 Type II</p>
                <p className="text-[10px] text-hms-textDim">{c.badges.certified}</p>
              </div>
            </LocaleLink>
            <LocaleLink href="/security" className="flex items-center gap-2 group">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-hms-blue/10 border border-hms-blue/20 group-hover:border-hms-blue/40 transition-colors">
                <Lock className="h-4 w-4 text-hms-blue" />
              </div>
                <div>
                  <p id="footer-badge-pci" className="text-[11px] font-bold text-hms-text leading-none">{t('shared.footer.pciDss')}</p>
                  <p className="text-[10px] text-hms-textDim">{c.badges.level1}</p>
                </div>
            </LocaleLink>
            <LocaleLink href="/security" className="flex items-center gap-2 group">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-hms-gold/10 border border-hms-gold/20 group-hover:border-hms-gold/40 transition-colors">
                <Globe className="h-4 w-4 text-hms-gold" />
              </div>
                <div>
                  <p id="footer-badge-gdpr" className="text-[11px] font-bold text-hms-text leading-none">{t('shared.footer.gdpr')}</p>
                  <p className="text-[10px] text-hms-textDim">{c.badges.compliant}</p>
                </div>
            </LocaleLink>
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-hms-teal/10 border border-hms-teal/20">
                <ShieldCheck className="h-4 w-4 text-hms-teal" />
              </div>
              <div>
                <p id="footer-badge-uptime" className="text-[11px] font-bold text-hms-text leading-none">99.9%</p>
                <p className="text-[10px] text-hms-textDim">{c.badges.uptime}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom Bar ── */}
      <div className="border-t border-hms-border">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[12px] text-hms-textDim" suppressHydrationWarning>
            &copy; {new Date().getFullYear()} {c.bottom.rights}
          </p>
          <div className="flex items-center gap-5">
            <LocaleLink href="/privacy" className="text-[12px] text-hms-textDim hover:text-hms-textSub transition-colors duration-200">{c.bottom.privacy}</LocaleLink>
            <LocaleLink href="/terms" className="text-[12px] text-hms-textDim hover:text-hms-textSub transition-colors duration-200">{c.bottom.terms}</LocaleLink>
            <LocaleLink href="/security" className="text-[12px] text-hms-textDim hover:text-hms-textSub transition-colors duration-200">{c.bottom.security}</LocaleLink>
            <LocaleLink href="/refund-policy" className="text-[12px] text-hms-textDim hover:text-hms-textSub transition-colors duration-200">{c.bottom.refundPolicy}</LocaleLink>
          </div>
        </div>
      </div>
    </footer>
  );
}
